import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class BlockedBib extends BaseObj {
    public Bib: number;
    public __bib: number;

    public EventID: number;
    public __eventid: number;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: { Bib?: number; EntityState?: EntityState; EventID?: number }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<BlockedBib>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('BlockedBib tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.Bib = values.Bib ?? 0;
        this.__bib = values.Bib ?? 0;
        Object.defineProperty(this, 'Bib', {
            get(): number {
                return this.__bib;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Bib', this.__bib, value);
                this.__bib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of BlockedBib from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: { Bib?: number; EventID?: number; EntityState?: 'Added' | 'Unchanged' }): BlockedBib {
        return new BlockedBib(values);
    }

    public static _FromServerJson(json: any): BlockedBib {
        return new BlockedBib({ Bib: json.Bib, EventID: json.EventID, EntityState: 'Unchanged' });
    }

    /**
     * Creates an instance of BlockedBib from another instance of BlockedBib.
     * @param other The BlockedBib you want to clone
     * @returns A cloned BlockedBib
     */
    public static FromOther(other: BlockedBib): BlockedBib {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return BlockedBib._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return BlockedBib.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('Bib' | 'EventID')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'BlockedBib';
}
